"use client";

import { useState } from "react";
import { runBulkAction } from "./bulkActions";
import type { BulkActionType } from "./types";

const ACTION_VERBS: Record<BulkActionType, string> = {
  archive: "archive",
  trash: "delete",
  markRead: "mark read",
  markUnread: "mark unread",
  star: "star",
  unstar: "remove the star from",
  keepNewest: "delete all but the newest of",
  moveToLabel: "move",
};

export type BulkProgress = { processed: number; total: number };

/** Confirms with the user (unless skipped), then runs a bulk action while
 *  tracking live progress — mirrors the app's runBulkActionWithProgress
 *  (confirm dialog + "Processed X of Y" progress dialog). Returns null
 *  (distinct from 0) if the user cancels the confirmation. */
export function useBulkActionWithProgress(accessToken: string) {
  const [progress, setProgress] = useState<BulkProgress | null>(null);

  async function run({
    query,
    actionType,
    historyLabel,
    moveToLabelName,
    estimatedCount,
    skipConfirmation,
  }: {
    query: string;
    actionType: BulkActionType;
    historyLabel: string;
    moveToLabelName?: string;
    estimatedCount?: number;
    skipConfirmation?: boolean;
  }): Promise<number | null> {
    if (!skipConfirmation) {
      const verb = ACTION_VERBS[actionType];
      const countText = estimatedCount != null ? `${estimatedCount} emails` : "these emails";
      const isDestructive = actionType === "trash" || actionType === "keepNewest";
      const confirmed = window.confirm(
        `${verb.charAt(0).toUpperCase()}${verb.slice(1)} ${countText} from "${historyLabel}"?${
          isDestructive ? " This cannot be undone." : ""
        }`,
      );
      if (!confirmed) return null;
    }

    setProgress({ processed: 0, total: 0 });
    try {
      return await runBulkAction({
        accessToken,
        query,
        actionType,
        historyLabel,
        moveToLabelName,
        onProgress: (processed, total) => setProgress({ processed, total }),
      });
    } finally {
      setProgress(null);
    }
  }

  return { run, progress };
}
